import React, { useState, useEffect } from 'react';
import {
  string, arrayOf, node, oneOfType, bool, shape, func,
} from 'prop-types';
import moment from 'moment';
import {
  ActivityContainer,
  VerticalAlign,
  ActivityDescriptor,
  ActivityHeader,
  ActivityTimeStamp,
  ContentContainer,
  IconContainer,
  CommentControlContainer,
  CommentIcon,
  CommentCount,
  ResolvedPill,
} from './activity-wrapper.style';

import { ACTIVITY_TYPES } from '../../../../../constants';
import { getActivityIcon } from '../../../../../utils/mappers';
import commentIcon from '../../../../../assets/icon-comment.svg';
import commentSelectedIcon from '../../../../../assets/icon-comment-selected.svg';
import ActivityThread from '../../activity-thread';
import activityShape from '../../../../../shapes/activity-shape';
import ImageIcon from '../../image-icon';

/**
 * Activity Reusable component
 * @param {object} param - Object containing props
 * @param {string} param.type - ACTIVITY_TYPE, type of warning to show
 * @param {object[]} param.headerContent - Content to show in the header.
 * @param {boolean} param.descriptorWarn - True will yield a red color for descriptor text
 * @param {element} param.children - Children elements
 * @param {string} param.timestamp - Time stamp of activity
 * @param {boolean} param.backgroundWarn - Sets background color to a red color
 * @param {string} param.testId - Test Id String
 * @param {boolean} param.canComment - Flag for if the activity in question allows commenting
 * @param {object[]} param.comments - A list of comments to display (if applicable)
 * @param {object} param.activity - The activity that is being wrapped
 * @param {func} param.scrollTo - handles scrolling to a activity in the parent
 * @return {element}
 */
const ActivityWrapper = ({
  type,
  headerContent,
  descriptorWarn,
  children,
  timestamp,
  backgroundWarn,
  testId,
  canComment,
  activity,
  scrollTo,
}) => {
  // State hook
  const [showComments, setShowComments] = useState(false);

  // Get Icon
  const icon = getActivityIcon(type);

  // Format timestamp
  const timeStamp = moment(timestamp).format('M/D HH:mm');
  const resolvedAtText = `Resolved (${moment(activity?.resolvedAt).format('M/D')})`;

  /**
   * Handles when the comment toggle is pressed
   * @returns {void}
   */
  const handleCommentTogglePressed = () => {
    setShowComments(!showComments);
    if (showComments) {
      scrollTo(activity?.eventId);
    }
  };

  /**
   * Scroll to the event every time the comments button is clicked
   */
  useEffect(() => {
    if (showComments) {
      scrollTo(activity.eventId);
    }
  });

  // Return component
  return (
    <>
      <ActivityContainer data-testid={testId} backgroundWarn={backgroundWarn} id={activity?.eventId}>
        <IconContainer>
          <ImageIcon src={icon} altString="Icon Indicator" width={25} height={25} offCenter />
        </IconContainer>
        <VerticalAlign>
          {headerContent.filter((c) => c !== null).map((content) => (
            <div key={content.text}>
              <ActivityHeader>
                {content.text}
                {content.descriptor ? ': ' : ''}
              </ActivityHeader>
              {content.descriptor
                && <ActivityDescriptor descriptorWarn={descriptorWarn}>{content.descriptor}</ActivityDescriptor>}
            </div>
          ))}
        </VerticalAlign>
        {activity?.resolvedAt && (<ResolvedPill>{resolvedAtText}</ResolvedPill>)}
        {children
          && (
            <ContentContainer>
              {children}
            </ContentContainer>
          )}
        {activity && canComment && (
          <CommentControlContainer onClick={handleCommentTogglePressed} canComment>
            <CommentIcon src={showComments ? commentSelectedIcon : commentIcon} width="17px" height="17px" />
            <CommentCount>{`${activity.comments?.length || 0} Comments`}</CommentCount>
          </CommentControlContainer>
        )}
        <ActivityTimeStamp canComment={canComment}>{timeStamp}</ActivityTimeStamp>
      </ActivityContainer>
      {activity && showComments && (
        <ActivityThread activity={{ ...activity, activity }} />
      )}
    </>
  );
};

ActivityWrapper.propTypes = {
  type: string,
  headerContent: arrayOf(shape({
    text: string,
    descriptor: string,
  })).isRequired,
  descriptorWarn: bool,
  backgroundWarn: bool,
  children: oneOfType([
    arrayOf(node),
    node,
  ]),
  timestamp: string.isRequired,
  testId: string,
  canComment: bool,
  // eslint-disable-next-line react/forbid-prop-types
  activity: activityShape,
  scrollTo: func,
};

ActivityWrapper.defaultProps = {
  type: ACTIVITY_TYPES.manualTrack,
  descriptorWarn: false,
  children: null,
  backgroundWarn: false,
  canComment: false,
  activity: null,
  testId: 'activity-wrapper',
  scrollTo: () => {},
};

export default ActivityWrapper;
